import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { authService } from '../services/authService';
import Layout from '../components/Layout';

const RegisterChimney = () => {
  const [importedClaimEmail, setImportedClaimEmail] = useState(null);
  const [claimSent, setClaimSent] = useState(false);
  const [claimSending, setClaimSending] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    city: '',
    phone: '',
    description: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { registerChimney } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (importedClaimEmail && e.target.name === 'email') {
      setImportedClaimEmail(null);
      setClaimSent(false);
    }
  };

  const handleRequestClaimLink = async () => {
    if (!importedClaimEmail) return;
    setClaimSending(true);
    setError('');
    try {
      await authService.requestClaimChimney(importedClaimEmail);
      setClaimSent(true);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        err.response?.data?.email?.[0] ||
        'Nie udało się wysłać linku. Spróbuj później.'
      );
    } finally {
      setClaimSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Hasła nie są identyczne');
      return;
    }

    if (formData.password.length < 8) {
      setError('Hasło musi mieć co najmniej 8 znaków');
      return;
    }

    if (!formData.companyName || !formData.city) {
      setError('Nazwa firmy i miasto są wymagane');
      return;
    }

    setLoading(true);

    try {
      await registerChimney(
        formData.firstName,
        formData.lastName,
        formData.email,
        formData.password,
        formData.confirmPassword,
        formData.companyName,
        formData.city,
        formData.phone,
        formData.description
      );
      setError('');
      alert('Konto kominiarza zostało utworzone! Sprawdź email w celu aktywacji konta.');
      navigate('/login');
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.code === 'imported_chimney') {
        setImportedClaimEmail(err.response.data.email ?? formData.email);
        setError('');
        setLoading(false);
        return;
      }
      console.error('Błąd rejestracji kominiarza:', err.response?.data);
      const errorMessages = [];
      if (err.response?.data) {
        Object.keys(err.response.data).forEach(key => {
          if (key === 'code') return;
          const errors = err.response.data[key];
          if (Array.isArray(errors)) {
            errorMessages.push(...errors.map(e => `${key}: ${e}`));
          } else if (typeof errors === 'string') {
            errorMessages.push(errors);
          } else if (typeof errors === 'object') {
            errorMessages.push(`${key}: ${JSON.stringify(errors)}`);
          }
        });
      }
      setError(
        errorMessages.length > 0 
          ? errorMessages.join('. ')
          : err.response?.data?.detail ||
            'Błąd rejestracji. Spróbuj ponownie.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-ik-black">
              Rejestracja kominiarza
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Utwórz konto jako kominiarz i zacznij oferować swoje usługi
            </p>
          </div>
          {importedClaimEmail && (
            <div className="mt-8 space-y-4 bg-amber-50 border border-amber-300 rounded-lg p-6">
              <h3 className="font-semibold text-ik-black">Ten adres jest już w bazie (wpis z importu)</h3>
              <p className="text-gray-700 text-sm">
                Na adres <strong>{importedClaimEmail}</strong> mamy już wpis kominiarza. Możesz przejąć ten wpis – wyślemy na niego link do ustawienia hasła. Tylko Ty masz dostęp do tej skrzynki, więc potwierdzimy w ten sposób, że to Ty.
              </p>
              {claimSent ? (
                <p className="text-green-700 text-sm font-medium">
                  Link został wysłany. Sprawdź skrzynkę (oraz folder „Spam”). Link jest ważny 7 dni.
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleRequestClaimLink}
                  disabled={claimSending}
                  className="px-4 py-2 bg-ik-orange text-white rounded-lg hover:bg-orange-600 transition disabled:opacity-50"
                >
                  {claimSending ? 'Wysyłanie…' : 'Wyślij link do przejęcia wpisu'}
                </button>
              )}
              <button
                type="button"
                onClick={() => { setImportedClaimEmail(null); setClaimSent(false); setError(''); }}
                className="block text-sm text-ik-orange hover:underline"
              >
                Zarejestruj się jako nowy wpis (inny email)
              </button>
            </div>
          )}

          <form className="mt-8 space-y-6 bg-white p-8 rounded-lg shadow-lg" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}
            
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-ik-black border-b pb-2">Dane osobowe</h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    Imię *
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-ik-orange focus:border-ik-orange sm:text-sm"
                    placeholder="Jan"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Nazwisko *
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-ik-orange focus:border-ik-orange sm:text-sm"
                    placeholder="Kowalski"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-ik-orange focus:border-ik-orange sm:text-sm"
                  placeholder="jan@example.com"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Hasło *
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-ik-orange focus:border-ik-orange sm:text-sm"
                    placeholder="Minimum 8 znaków"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Powtórz hasło *
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-ik-orange focus:border-ik-orange sm:text-sm"
                    placeholder="Powtórz hasło"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-ik-black border-b pb-2">Dane firmy</h3>
              
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                  Nazwa firmy *
                </label>
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  required
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-ik-orange focus:border-ik-orange sm:text-sm"
                  placeholder="Kominiarz Jan Kowalski"
                  value={formData.companyName}
                  onChange={handleChange}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                    Miasto *
                  </label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    required
                    className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-ik-orange focus:border-ik-orange sm:text-sm"
                    placeholder="Warszawa"
                    value={formData.city}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Telefon
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="text"
                    className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-ik-orange focus:border-ik-orange sm:text-sm"
                    placeholder="123456789"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Opis firmy
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="4"
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-ik-orange focus:border-ik-orange sm:text-sm"
                  placeholder="Opisz swoje usługi, doświadczenie..."
                  value={formData.description}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-ik-orange hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ik-orange disabled:opacity-50"
              >
                {loading ? 'Rejestracja...' : 'Zarejestruj się jako kominiarz'}
              </button>
            </div>

            <div className="text-center space-y-2">
              <p className="text-sm text-gray-600">
                Masz już konto?{' '}
                <Link to="/login" className="font-medium text-ik-orange hover:text-orange-600">
                  Zaloguj się
                </Link>
              </p>
              <p className="text-sm text-gray-600">
                Twoje dane są w bazie z importu?{' '}
                <Link to="/request-claim-chimney" className="font-medium text-ik-orange hover:text-orange-600">
                  Przejmij wpis
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default RegisterChimney;
